import { CommentType } from '@mesa/core/drizzle';
import { type ReactionEmoji } from '@mesa/core/reactions';
import { SmilePlusIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { ReactionButton } from './reaction-button';
import { ReactionPicker } from './reaction-picker';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { groupReactionsByEmoji, useReactions } from '@/hooks/use-reactions';
import { cn } from '@/lib/utils';
import { useUser } from '@/services/user';

interface ReactionBarProps {
  subjectId: number;
  commentType: CommentType;
  className?: string;
}

export function ReactionBar({ subjectId, commentType, className }: ReactionBarProps) {
  const { user } = useUser();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const {
    reactions,
    addIssueCommentReaction,
    addReviewCommentReaction,
    addReviewBodyReaction,
    removeIssueCommentReaction,
    removeReviewCommentReaction,
    removeReviewBodyReaction,
    isLoading,
  } = useReactions({
    subjectId,
    commentType,
  });

  const summaries = useMemo(() => groupReactionsByEmoji(reactions, user.id), [reactions]);

  const handleAddReaction = useCallback(
    async (emoji: ReactionEmoji) => {
      if (commentType === 'issue') {
        console.log('addIssueCommentReaction', emoji);
        await addIssueCommentReaction(emoji);
      } else if (commentType === 'review') {
        console.log('addReviewCommentReaction', emoji);
        await addReviewCommentReaction(emoji);
      } else if (commentType === 'review_body') {
        console.log('addReviewBodyReaction', emoji);
        await addReviewBodyReaction(emoji);
      }
    },
    [commentType, addIssueCommentReaction, addReviewCommentReaction, addReviewBodyReaction]
  );

  const handleRemoveReaction = useCallback(
    async (gid: string) => {
      if (commentType === 'issue') {
        console.log('removeIssueCommentReaction', gid);
        await removeIssueCommentReaction(gid);
      } else if (commentType === 'review') {
        console.log('removeReviewCommentReaction', gid);
        await removeReviewCommentReaction(gid);
      } else if (commentType === 'review_body') {
        console.log('removeReviewBodyReaction', gid);
        await removeReviewBodyReaction(gid);
      }
    },
    [commentType, removeIssueCommentReaction, removeReviewCommentReaction, removeReviewBodyReaction]
  );

  const handleEmojiSelect = useCallback(
    async (emoji: ReactionEmoji) => {
      try {
        await handleAddReaction(emoji);
        setIsPickerOpen(false);
      } catch (error) {
        console.error('Failed to add reaction:', error);
        toast.error('Failed to add reaction');
      }
    },
    [handleAddReaction]
  );

  const handleReactionToggle = useCallback(
    async (emoji: ReactionEmoji) => {
      const currentUserReaction = reactions.find((reaction) => reaction.emoji === emoji && reaction.userId === user.id);
      try {
        if (currentUserReaction) {
          await handleRemoveReaction(currentUserReaction.gid);
        } else {
          await handleAddReaction(emoji);
        }
      } catch (error) {
        console.error('Failed to toggle reaction:', error);
        toast.error('Failed to toggle reaction');
      }
    },
    [handleRemoveReaction, handleAddReaction, reactions, user.id]
  );

  // Don't render anything if loading and no reactions
  if (isLoading) {
    return null;
  }

  return (
    <div className={cn('flex items-center gap-1 flex-wrap', className)}>
      {/* Existing reactions */}
      {summaries.map((reaction) => (
        <ReactionButton
          key={reaction.emoji}
          reaction={reaction}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            handleReactionToggle(reaction.emoji);
          }}
          disabled={false}
        />
      ))}

      {/* Add reaction button */}
      <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 px-2 py-1 text-xs font-medium transition-colors',
              'border border-dashed border-gray-300 hover:border-gray-400',
              'bg-transparent hover:bg-gray-50',
              'text-gray-500 hover:text-gray-700'
            )}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
            }}
          >
            <SmilePlusIcon className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" side="top" align="start">
          <ReactionPicker onEmojiSelect={handleEmojiSelect} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
